import React, { createContext, useState, useCallback, useEffect } from "react";
import SnackbarMessage from "../components/common/SnackbarMessage";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Add message state
  const [message, setMessage] = useState({
    open: false,
    message: "",
    severity: "success",
    title: "",
  });

  const closeMessage = () => {
    setMessage((prev) => ({ ...prev, open: false }));
  };

  const showMessage = (messageConfig) => {
    setMessage({ ...messageConfig, open: true });
  };

  const addToCart = useCallback((item) => {
    setCartItems((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i,
        );
      }
      showMessage({
        message: `${item.title} added to your cart successfully!`,
        severity: "success",
        title: "Added to Cart",
      });
      return [...prev, { ...item, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCartItems((prev) => {
      const itemToRemove = prev.find((item) => item.id === itemId);
      if (itemToRemove) {
        showMessage({
          message: `${itemToRemove.title} removed from your cart`,
          severity: "info",
          title: "Removed from Cart",
        });
      }
      return prev.filter((item) => item.id !== itemId);
    });
  }, []);

  const isInCart = useCallback(
    (itemId) => {
      return cartItems.some((item) => item.id === itemId);
    },
    [cartItems],
  );

  const updateQuantity = useCallback((itemId, quantity) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, quantity: Math.max(1, quantity) }
          : item,
      ),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem("cart");
  }, []);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    isInCart,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      <SnackbarMessage
        open={message.open}
        onClose={closeMessage}
        message={message.message}
        severity={message.severity}
        title={message.title}
      />
    </CartContext.Provider>
  );
};
